import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { loginSettingsAPI } from '../../services/api';
import '../../assets/styles/login.css';

// Default slides (Backend-ээс татаж чадаагүй үед)
const defaultSlides = [
  {
    _id: '1',
    title: 'Тавтай морил!',
    description: 'Pingo Express нь таны бизнесийн хүргэлтийг хялбар, хурдан, найдвартай болгоно.',
    stats: { label: 'НИЙТ ХҮРГЭЛТ', value: '0', icon: '📦' },
    chart: { percent: '0%', label: 'Хүргэлт' }
  }
];

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Settings from backend
  const [slides, setSlides] = useState(defaultSlides);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [sliderInterval, setSliderInterval] = useState(8000);

  // Fetch login settings from backend
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounted
    
    const fetchSettings = async () => {
      try {
        const response = await loginSettingsAPI.getLoginSettings();
        
        console.log('📡 Login settings response:', response);
        
        if (response.success && isMounted) {
          const { logoUrl: logo, slides: fetchedSlides, sliderConfig } = response.data;
          
          // Set logo
          if (logo) {
            // Handle JSON string or plain string
            let parsedLogo = logo;
            if (typeof logo === 'string' && logo.startsWith('"')) {
              try {
                parsedLogo = JSON.parse(logo);
              } catch (e) {
                parsedLogo = logo;
              }
            }
            setLogoUrl(parsedLogo);
            console.log('🖼️ Logo URL:', parsedLogo);
          }
          
          // Set slides
          if (fetchedSlides && fetchedSlides.length > 0) {
            setSlides(fetchedSlides);
            console.log('📑 Slides:', fetchedSlides);
          }
          
          // Set slider interval
          if (sliderConfig?.interval) {
            let interval = sliderConfig.interval;
            if (typeof interval === 'string') {
              interval = parseInt(interval);
            }
            setSliderInterval(interval);
            console.log('⏱️ Slider interval:', interval);
          }
        }
      } catch (error) {
        console.error('❌ Failed to fetch login settings:', error);
        // Use default values on error
      }
    };

    fetchSettings();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Empty deps - run only once on mount

  // Auto slide
  useEffect(() => {
    if (slides.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, sliderInterval);
    
    return () => clearInterval(interval);
  }, [slides.length, sliderInterval]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { username, password } = formData;

    if (!username || !password) {
      setError('Хэрэглэгчийн нэр болон нууц үг оруулна уу');
      setLoading(false);
      return;
    }

    try {
      const result = await login(username, password);
      setLoading(false);

      if (result.success) {
        const userType = result.user?.user_type;

        console.log('✅ Амжилттай нэвтэрлээ:', {
          user_type: userType,
          username: result.user?.username,
          full_name: result.user?.full_name
        });

        switch (userType) {
          case 'system_admin':
            navigate('/admin/dashboard');
            break;
          case 'company_user':
            navigate('/company/dashboard');
            break;
          case 'shop_user':
            navigate('/shop/dashboard');
            break;
          case 'driver':
            navigate('/driver/deliveries');
            break;
          default:
            console.error('❌ Тодорхойгүй user_type:', userType);
            setError('Тодорхойгүй хэрэглэгчийн төрөл');
            navigate('/');
        }
      } else {
        setError(result.message || 'Нэвтрэхэд алдаа гарлаа');
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      setLoading(false);
      setError(err.response?.data?.message || 'Нэвтрэхэд алдаа гарлаа');
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide] || defaultSlides[0];

  // Get full logo URL - ЗАСВАРЛАСАН! 🎯
  // useMemo ашиглаж давтан тооцохоос сэргийлнэ
  const logoSrc = useMemo(() => {
    console.log('🖼️ Computing logo URL for:', logoUrl);
    
    if (!logoUrl) return '/logo.png';
    
    // Remove quotes if JSON string
    let cleanUrl = logoUrl;
    if (typeof logoUrl === 'string') {
      cleanUrl = cleanUrl.replace(/^["']|["']$/g, '').trim();
    }
    
    console.log('🧹 Cleaned URL:', cleanUrl);
    
    // If full URL with http/https
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      console.log('✅ Using full HTTP URL:', cleanUrl);
      return cleanUrl;
    }
    
    // If it's from backend uploads folder
    if (cleanUrl.startsWith('/uploads')) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
      // Remove /api/v1 or /api from the end to get base URL
      const baseUrl = apiUrl.replace(/\/api(\/v1)?$/, '');
      const fullUrl = `${baseUrl}${cleanUrl}`;
      console.log('✅ Backend upload URL:', fullUrl);
      return fullUrl;
    }
    
    // Public folder file (relative path)
    console.log('✅ Public folder URL:', cleanUrl);
    return cleanUrl;
  }, [logoUrl]); // Only recompute when logoUrl changes

  return (
    <div className="login-container">
      {/* ============================================
          LEFT SIDE - SLIDER SECTION
          ============================================ */}
      <div className="login-slider-section">
        {/* Slider content */}
        <div className="slider-content">
          {/* Showcase cards */}
          <div className="showcase-cards">
            {/* Balance/Stats Card */}
            <div className="card-balance slide-animate" key={`balance-${currentSlide}`}>
              <div className="card-balance-header">
                <div className="card-balance-icon">
                  {currentSlideData.stats?.icon || '📦'}
                </div>
                <span className="card-balance-label">
                  {currentSlideData.stats?.label || 'СТАТИСТИК'}
                </span>
              </div>
              <div className="card-balance-amount">
                {currentSlideData.stats?.value || '0'}
              </div>
            </div>

            {/* Chart Card */}
            <div className="card-chart">
              <div className="chart-circle">
                <svg viewBox="0 0 100 100">
                  <circle className="chart-circle-bg" cx="50" cy="50" r="45" />
                  <circle className="chart-circle-progress chart-circle-blue" cx="50" cy="50" r="45" />
                  <circle className="chart-circle-progress chart-circle-green" cx="50" cy="50" r="45" />
                  <circle className="chart-circle-progress chart-circle-red" cx="50" cy="50" r="45" />
                  <circle className="chart-circle-progress chart-circle-yellow" cx="50" cy="50" r="45" />
                </svg>
                <div className="chart-center">
                  <div className="chart-percent">
                    {currentSlideData.chart?.percent || '0%'}
                  </div>
                  <div className="chart-label">
                    {currentSlideData.chart?.label || 'Үзүүлэлт'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Card */}
            <div className="card-action">
              <div className="card-action-icon">+</div>
              <div className="card-action-title">Шинэ хүргэлт</div>
              <div className="card-action-subtitle">үүсгэх</div>
            </div>
          </div>

          {/* Slide text */}
          <div className="slide-text slide-animate" key={`text-${currentSlide}`}>
            <h2 className="slide-title">{currentSlideData.title}</h2>
            <p className="slide-description">
              {currentSlideData.description}
            </p>
          </div>

          {/* Navigation */}
          {slides.length > 1 && (
            <div className="slide-nav">
              <button className="nav-btn" onClick={prevSlide} aria-label="Өмнөх">
                ‹
              </button>

              <div className="slide-indicators">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`${index + 1}-р слайд`}
                  />
                ))}
              </div>

              <button className="nav-btn" onClick={nextSlide} aria-label="Дараах">
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ============================================
          RIGHT SIDE - LOGIN FORM SECTION
          ============================================ */}
      <div className="login-form-section">
        <div className="login-form-container">
          {/* Header with Logo */}
          <div className="form-header">
            <div className="form-header-logo">
              <img 
                src={logoSrc} 
                alt="Pingo Express"
                onError={(e) => {
                  console.error('❌ Logo татахад алдаа:', e.target.src);
                  e.target.src = '/logo.png'; // Fallback
                }}
                onLoad={() => {
                  console.log('✅ Logo амжилттай татагдлаа:', logoSrc);
                }}
              />
            </div>
            <h1 className="form-title">Тавтай морил!</h1>
            <p className="form-subtitle">Системд нэвтрэхийн тулд мэдээллээ оруулна уу</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <div className="error-content">
                <h4>Алдаа гарлаа</h4>
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div className="input-group">
              <label className="input-label" htmlFor="username">
                Хэрэглэгчийн нэр
              </label>
              <div className="input-wrapper">
                <span className="input-icon">✉️</span>
                <input
                  id="username"
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="login-input"
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div className="input-group">
              <label className="input-label" htmlFor="password">
                Нууц үг
              </label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Хамгийн багадаа 8 тэмдэгт"
                  className="login-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Нууц үг нуух' : 'Нууц үг харах'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="forgot-password-row">
              <Link to="/forgot-password" className="forgot-password-link">
                Нууц үг мартсан?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="login-btn"
            >
              <span className="login-btn-content">
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Нэвтэрч байна...
                  </>
                ) : (
                  'Нэвтрэх'
                )}
              </span>
            </button>
          </form>

          {/* Register Link */}
          <div className="register-row">
            <span className="register-text">
              Бүртгэлгүй юу?
              <Link to="/register" className="register-link">
                Бүртгүүлэх
              </Link>
            </span>
          </div>

          {/* Footer */}
          <div className="login-footer">
            <p>© {new Date().getFullYear()} Pingo Express. Бүх эрх хуулиар хамгаалагдсан.</p>
            <p className="developed-by">
              Developed by{' '}
              <a href="mailto:info@prolink.mn" className="prolink-link">
                Prolink Team
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;